<template>
  <div class="flex w-full relative bg-gray-50 dark:bg-navy-1000">
    <ReportsSidebar
      @select="selectSideBarOption"
    />
    <!-- ACTIVE REPORTS PAGE -->
    <div
      id="reportsScrollable"
      class="flex flex-col w-full bg-gray-50 dark:bg-navy-1000"
    >
      <!-- REPORTS -->
      <div class="flex-col items-center flex">
        <div
          class="sectionHeader flex items-center justify-center w-full z-20 lg:z-15 lg:transition-all duration-200 transform shadow-sm "
          :class="headerScrolled"
        >
          <ReportsLogo class="transition-all duration-200" :class="logoClass" />
        </div>
      </div>
      <!-- SELECTED REPORTS COMPONENT -->
      <div :class="pageClass">
        <component :is="currentReportComponent" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from "vuex"
import { getUnitOfMeasureQuery } from "@/services/Lookups.js"

import { defineAsyncComponent } from "vue"
// Async-loaded reports (each becomes a separate chunk)
const WashedSummaryReport = defineAsyncComponent({
  loader: () => import(/* @vite-ignore */ "./WashedReports/WashedSummaryReport.vue"),
    loadingComponent: LoadingSpinnerPage,
})
const WashedDailyReport = defineAsyncComponent({
  loader: () => import(/* @vite-ignore */ "./WashedReports/WashedDailyReport.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const WashedHourlyReport = defineAsyncComponent({
  loader: () => import(/* @vite-ignore */ "./WashedReports/WashedHourlyReport.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const WashedDayOfWeekReport = defineAsyncComponent({
  loader: () => import(/* @vite-ignore */ "./WashedReports/WashedDayOfWeekReport.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const WeatherImpactReport = defineAsyncComponent({
  loader: () => import(/* @vite-ignore */ "./WashedReports/WeatherImpactReport.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const AssetsReport = defineAsyncComponent({
  loader: () => import(/* @vite-ignore */ "./AssetCostOfOwnership/AssetsReport.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const PrevMaintenanceCompletionReport = defineAsyncComponent({
  loader: () => import(/* @vite-ignore */ "./PreventiveMaintenance/PrevMaintenanceCompletionReport.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const PrevMaintenanceAuditReport = defineAsyncComponent({
  loader: () => import(/* @vite-ignore */ "./PreventiveMaintenance/PrevMaintenanceAuditReport.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const AssetsWithWorkOrdersPage = defineAsyncComponent({
  loader: () => import(/* @vite-ignore */ "./AssetCostOfOwnership/AssetsWithWorkOrdersPage.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const CurrentInventory = defineAsyncComponent({
  loader: () => import(/* @vite-ignore */ "./Inventory/CurrentInventory.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const RevenueBreakdownReport = defineAsyncComponent({
  loader: () => import(/* @vite-ignore */ "./Washlink/RevenueBreakdownReport.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const PurchaseOrders = defineAsyncComponent({
  loader: () => import(/* @vite-ignore */ "./PurchaseOrders/PurchaseOrders.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const ChemicalStatusReportPage = defineAsyncComponent({
  loader: () => import(/* @vite-ignore */ "./Chemical/ChemicalStatusReportPage.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const PackageConsumption = defineAsyncComponent({
  loader: () => import(/* @vite-ignore */ "./Chemical/PackageConsumptionReportPage.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const ProductConsumption = defineAsyncComponent({
  loader: () => import(/* @vite-ignore */ "./Chemical/ProductConsumptionReportPage.vue"),
  loadingComponent: LoadingSpinnerPage,
})
import ReportsDashboard from "./ReportsDashboard.vue"
import ReportsSidebar from "../components/ReportsSidebar.vue"
import ReportsLogo from "@/assets/Reports_Navigation_Icon_Full_Color.svg"
import LoadingSpinnerPage from "@/components/Loading/LoadingSpinner/LoadingSpinnerPage.vue"

export default {
  name: "ReportsPage",
  components: {
    ReportsDashboard,
    ReportsSidebar,
    ReportsLogo,
    LoadingSpinnerPage
  },
  computed: {
    ...mapState([
      "headers",
      "screen",
      "orientation"
    ]),
    ...mapGetters(["showingModuleHeader", "atTopOfPage"]),
    currentReportComponent() {
      const path = this.$route.path

      if (path.includes("/washed/summary")) return WashedSummaryReport
      if (path.includes("/washed/daily")) return WashedDailyReport
      if (path.includes("/washed/hourly")) return WashedHourlyReport
      if (path.includes("/washed/day_of_week")) return WashedDayOfWeekReport
      if (path.includes("/washed/weather_impact")) return WeatherImpactReport
      if (path.includes("/cost_of_ownership")) return AssetsReport
      if (path.includes("detailed_cost_of_ownership")) return AssetsWithWorkOrdersPage
      if (path.includes("inventory_state_report")) return CurrentInventory
      if (path.includes("revenue_breakdown")) return RevenueBreakdownReport
      if (path.includes("purchase_orders")) return PurchaseOrders
      if (path.includes("chemical_status_report")) return ChemicalStatusReportPage
      if (path.includes("package_consumption")) return PackageConsumption
      if (path.includes("product_consumption")) return ProductConsumption
      if (path.includes("pm_completion")) return PrevMaintenanceCompletionReport
      if (path.includes("pm_audit_report")) return PrevMaintenanceAuditReport

      // default to dashboard
      return ReportsDashboard
    },
    headerScrolled() {
      if (this.isMobileLandscape && !this.isTablet && !this.screen.xl) return "hidden"
      let className = "md:fixed lg:shadow-md bg-gray-50 dark:bg-navy-1000 lg:bg-white-off "
      if (this.atTopOfPage || !this.screen.lg) {
        className += "h-16 lg:h-24 shadow-sm "
      }
      else className += "shadow-lg h-24 lg:h-16 "
      if (!this.showingModuleHeader && this.screen.lg) className += "-translate-y-full md:translate-y-0 "
      else className += "-translate-y-0 "
      return className
    },
    pageClass() {
      let classes = "flex flex-col relative "
      if (!this.isMobileLandscape || (this.isTablet)) classes += "md:mt-16 lg:mt-24 "
      return classes
    },
    path() {
      return this.$route.name
    },
    logoClass() {
      if (this.atTopOfPage && this.screen.lg) return "h-14 lg:h-20 py-2 my-0"
      else return "h-14 lg:h-16 py-2" 
    },
    isMobile() {
      return /Mobi/i.test(window.navigator.userAgent)
    },
    isMobileLandscape() {
      return this.isMobile && this.orientation.landscape
    },
    isMobilePortrait() {
      return this.isMobile && this.orientation.portrait
    },
    isTablet() {
      return /ipad|tablet/i.test(window.navigator.userAgent.toLowerCase())
    },
  },
  watch: {
    path(val) {
      this.setSTPage(val)
    }
  },
  mounted() {
    this.showModuleHeader(true)
    this.loadUnitsOfMeasure()
  },
  methods: {
    ...mapMutations([
      "setUnits",
      "setUnitOptions",
      "setSTPage",
      "setPrompt",
      "showModuleHeader"
    ]),
    selectSideBarOption(option) {
      this.setSTPage(option)
    },
    loadUnitsOfMeasure() {
      let query = {
        page: -1
      }
      getUnitOfMeasureQuery(query, this.headers)
      .then(response => {
        if (response.status === 200) {
          let unitObj = {}
          let unitArr = []
          for (let unit of response.data.results) {
            unit.name = `${unit.name} (${unit.abbreviation})`
            unitObj[unit.id] = unit
            unitArr.push(unit)
          }
          this.setUnits(unitObj)
          this.setUnitOptions(unitArr)
        }
        else {
          console.log(response)
        }
      }).catch(error => {
        console.log(error)
      })
    },
  },
}
</script>